// ============================================================================
// COMPLIANCE USER - ACTIVE FRAMEWORKS PAGE
// ============================================================================
// Purpose: View frameworks assigned to the user's company
// Mental model: "Show me what compliance frameworks I'm working on."
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  FileText,
  CheckSquare,
  Clock,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { complianceExecutionApi } from '@domains/compliance/execution/api/complianceExecution.api';
import PageInfoButton from '@/components/PageInfoButton';

interface FrameworkSummary {
  frameworkId: number;
  frameworkName: string;
  totalPages: number;
  completedPages: number;
  inProgressPages: number;
  pendingPages: number;
}

const ActiveFrameworks: React.FC = () => {
  const { hasPermission } = useAuth();
  const [frameworks, setFrameworks] = useState<FrameworkSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadFrameworks();
  }, []);

  const loadFrameworks = async () => {
    setIsLoading(true);
    try {
      const pages = await complianceExecutionApi.getAssignedPages();

      // Group pages by framework
      const frameworkMap = new Map<number, FrameworkSummary>();

      pages.forEach(page => {
        const frameworkId = page.framework_id;
        const frameworkName = page.framework_name || `Framework ${frameworkId}`;

        if (!frameworkMap.has(frameworkId)) {
          frameworkMap.set(frameworkId, {
            frameworkId,
            frameworkName,
            totalPages: 0,
            completedPages: 0,
            inProgressPages: 0,
            pendingPages: 0
          });
        }

        const summary = frameworkMap.get(frameworkId)!;
        summary.totalPages++;

        if (page.status === 'submitted' || page.status === 'approved_for_audit') {
          summary.completedPages++;
        } else if (page.status === 'in_progress') {
          summary.inProgressPages++;
        } else {
          summary.pendingPages++;
        }
      });

      setFrameworks(Array.from(frameworkMap.values()));
    } catch (error) {
      console.error('Error loading frameworks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getProgressPercentage = (completed: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 75) return 'bg-green-500';
    if (percentage >= 50) return 'bg-blue-500';
    if (percentage >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (!hasPermission('frameworks.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 p-8">
          <AlertTriangle className="text-red-500 mb-3" size={36} />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-500 text-sm">You don't have permission to view frameworks.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading frameworks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Active Frameworks</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm">Compliance frameworks assigned to your company</p>
          </div>
          <button
            onClick={loadFrameworks}
            className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Frameworks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {frameworks.length === 0 ? (
            <div className="col-span-full bg-white rounded-3xl border border-slate-100 p-12">
              <FileText className="text-slate-300 mb-3" size={40} />
              <h3 className="text-base font-semibold text-slate-900 mb-1">No frameworks found</h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                You don't have any compliance frameworks assigned to you yet.
              </p>
            </div>
          ) : (
            frameworks.map(framework => {
              const progress = getProgressPercentage(framework.completedPages, framework.totalPages);
              const progressColor = getProgressColor(progress);

              return (
                <div
                  key={framework.frameworkId}
                  className="bg-white rounded-3xl border border-slate-100 hover:border-slate-300 transition-colors overflow-hidden"
                >
                  {/* Framework Header */}
                  <div className="px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <FileText className="text-blue-600 shrink-0" size={18} />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 text-sm truncate">
                          {framework.frameworkName}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {framework.totalPages} page{framework.totalPages !== 1 ? 's' : ''} assigned
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="px-5 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-slate-600">Progress</span>
                      <span className="text-xs font-bold text-slate-900">{progress}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`${progressColor} h-full rounded-full transition-all duration-500`}
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="px-5 pb-4">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-green-50 rounded-lg p-2.5 text-center">
                        <CheckSquare className="text-green-600 mx-auto mb-0.5" size={14} />
                        <p className="text-sm font-bold text-green-700">{framework.completedPages}</p>
                        <p className="text-[10px] text-green-600">Done</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                        <Clock className="text-blue-600 mx-auto mb-0.5" size={14} />
                        <p className="text-sm font-bold text-blue-700">{framework.inProgressPages}</p>
                        <p className="text-[10px] text-blue-600">In Progress</p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-2.5 text-center">
                        <AlertTriangle className="text-yellow-600 mx-auto mb-0.5" size={14} />
                        <p className="text-sm font-bold text-yellow-700">{framework.pendingPages}</p>
                        <p className="text-[10px] text-yellow-600">Pending</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ActiveFrameworks;
